import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivateFn, UrlTree } from '@angular/router';
import { map } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { PermisosService } from '../services/permisos.service';

function redirectFallback(router: Router, permisosService: PermisosService): UrlTree {
  const modulos: Array<{ modulo: string; ruta: string; queryParams?: Record<string, string> }> = [
    { modulo: 'movimientos', ruta: '/movimientos' },
    { modulo: 'facturas', ruta: '/movimientos', queryParams: { tab: 'fac' } },
    { modulo: 'feed', ruta: '/espacio', queryParams: { tab: 'actividad' } },
    { modulo: 'torre', ruta: '/espacio', queryParams: { tab: 'torre' } },
    { modulo: 'cuentas', ruta: '/cuentas' },
    { modulo: 'dashboard', ruta: '/dashboard' },
    { modulo: 'cierre', ruta: '/dashboard', queryParams: { tab: 'cierre' } },
    { modulo: 'fiscal', ruta: '/dashboard', queryParams: { tab: 'fiscal' } },
    { modulo: 'clientes', ruta: '/catalogos', queryParams: { tab: 'clientes' } },
    { modulo: 'proveedores', ruta: '/catalogos', queryParams: { tab: 'proveedores' } },
    { modulo: 'vehiculos', ruta: '/catalogos', queryParams: { tab: 'vehiculos' } },
    { modulo: 'usuarios', ruta: '/configuracion', queryParams: { tab: 'usuarios' } },
    { modulo: 'sat', ruta: '/configuracion', queryParams: { tab: 'sat' } },
    { modulo: 'sistema', ruta: '/configuracion', queryParams: { tab: 'sistema' } },
    { modulo: 'factura', ruta: '/factura' }
  ];
  for (const { modulo, ruta, queryParams } of modulos) {
    if (permisosService.tienePermisoModulo(modulo)) {
      return router.createUrlTree([ruta], queryParams ? { queryParams } : undefined);
    }
  }
  return router.createUrlTree(['/espacio'], { queryParams: { tab: 'perfil' } });
}

export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const permisosService = inject(PermisosService);

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/login']);
  }

  const requiredPermission = route.data['permission'] as { modulo: string; accion: string };

  return permisosService.whenReady().pipe(
    map(() => {
      if (!authService.isAuthenticated()) {
        return router.createUrlTree(['/login']);
      }

      if (!requiredPermission) {
        return true;
      }

      if (permisosService.tienePermiso(requiredPermission.modulo, requiredPermission.accion)) {
        return true;
      }

      return redirectFallback(router, permisosService);
    })
  );
};

export const moduleGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const permisosService = inject(PermisosService);

  if (!authService.isAuthenticated()) {
    return router.createUrlTree(['/login']);
  }

  const requiredModules = route.data['modules'] as string[] | undefined;
  const requiredModule = route.data['module'] as string | undefined;

  return permisosService.whenReady().pipe(
    map(() => {
      if (!authService.isAuthenticated()) {
        return router.createUrlTree(['/login']);
      }

      if (requiredModules?.length) {
        if (requiredModules.some((m) => permisosService.tienePermisoModulo(m))) {
          return true;
        }
        return redirectFallback(router, permisosService);
      }

      if (!requiredModule) {
        return true;
      }

      if (permisosService.tienePermisoModulo(requiredModule)) {
        return true;
      }

      return redirectFallback(router, permisosService);
    })
  );
};

/** Redirige rutas viejas a /movimientos?tab=... */
export function redirectToMovimientosTab(tab: string): CanActivateFn {
  return () => inject(Router).createUrlTree(['/movimientos'], { queryParams: { tab } });
}

/** Redirige rutas viejas a /catalogos?tab=... */
export function redirectToCatalogosTab(tab: string): CanActivateFn {
  return () => inject(Router).createUrlTree(['/catalogos'], { queryParams: { tab } });
}

/** Redirige rutas viejas a /configuracion?tab=... */
export function redirectToConfiguracionTab(tab: string): CanActivateFn {
  return () => inject(Router).createUrlTree(['/configuracion'], { queryParams: { tab } });
}

/** Redirige rutas viejas a /espacio?tab=... */
export function redirectToEspacioTab(tab: string): CanActivateFn {
  return () => inject(Router).createUrlTree(['/espacio'], { queryParams: { tab } });
}
